import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card } from "../components/ui";

const TIPO_IMPIANTO_OPTIONS = [
  { value: 100000000, label: "Fotovoltaico", icon: "☀️" },
];

const STATO_OPTIONS = [
  { value: 100000000, label: "Funzionante", icon: "✅" },
  { value: 100000001, label: "Guasto", icon: "❌" },
  { value: 100000002, label: "In manutenzione", icon: "🔧" },
  { value: 100000003, label: "Dismesso", icon: "🚫" },
];

const TIPO_FOTOVOLTAICO = "100000000";

const REGIME_INCENTIVO_OPTIONS = [
  { value: 100000000, label: "Scambio sul posto" },
  { value: 100000001, label: "Ritiro dedicato" },
  { value: 100000002, label: "Incentivo RID" },
  { value: 100000003, label: "Autoconsumo" },
  { value: 100000004, label: "Nessuno" },
];

function NuovoImpiantoPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  const isEdit = !!id;
  const rsaIdParam = searchParams.get("rsaId");
  const immobileIdParam = searchParams.get("immobileId");
  const tipoParam = searchParams.get("tipo");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rsaList, setRsaList] = useState([]);
  const [immobiliList, setImmobiliList] = useState([]);
  const [fornitoriList, setFornitoriList] = useState([]);
  const [loadingImmobili, setLoadingImmobili] = useState(false);

  const [formData, setFormData] = useState({
    nome: "",
    tipoImpianto: tipoParam || TIPO_FOTOVOLTAICO,
    stato: 100000000,
    marca: "",
    modello: "",
    dataInstallazione: "",
    dataScadenzaGaranzia: "",
    note: "",
    rsaId: rsaIdParam || "",
    immobileId: immobileIdParam || "",
    fornitoreAcquistoId: "",
    fornitoreManutenzioneId: "",
    potenzaInstallata: "",
    numeroPraticaGse: "",
    regimeIncentivo: "",
    dataAllaccioRete: "",
    gestoreRete: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const rsaData = await apiRequest("/rsa");
        setRsaList(rsaData.value || []);

        const fornitoriData = await apiRequest("/fornitori");
        setFornitoriList(fornitoriData.value || []);

        if (isEdit) {
          const data = await apiRequest(`/impianti/${id}`);
          setFormData({
            nome: data.nome || "",
            tipoImpianto: data.tipoImpianto ?? TIPO_FOTOVOLTAICO,
            stato: data.stato ?? 100000000,
            marca: data.marca || "",
            modello: data.modello || "",
            dataInstallazione: data.dataInstallazione?.split("T")[0] || "",
            dataScadenzaGaranzia:
              data.dataScadenzaGaranzia?.split("T")[0] || "",
            note: data.note || "",
            rsaId: data.rsaId || "",
            immobileId: data.immobileId || "",
            fornitoreAcquistoId: data.fornitoreAcquistoId || "",
            fornitoreManutenzioneId: data.fornitoreManutenzioneId || "",
            potenzaInstallata: data.potenzaInstallata ?? "",
            numeroPraticaGse: data.numeroPraticaGse || "",
            regimeIncentivo: data.regimeIncentivo ?? "",
            dataAllaccioRete: data.dataAllaccioRete?.split("T")[0] || "",
            gestoreRete: data.gestoreRete || "",
          });

          if (data.rsaId) {
            const immobiliData = await apiRequest(
              `/rsa/${data.rsaId}/immobili`,
            );
            setImmobiliList(immobiliData.value || []);
          }
        } else if (rsaIdParam) {
          const immobiliData = await apiRequest(`/rsa/${rsaIdParam}/immobili`);
          setImmobiliList(immobiliData.value || []);
        }
      } catch (error) {
        console.error("Error loading data:", error);
        showToast("Errore nel caricamento dei dati", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [id, isEdit, rsaIdParam, apiRequest, showToast]);

  useEffect(() => {
    const loadRsaImmobili = async () => {
      if (!formData.rsaId) {
        setImmobiliList([]);
        setFormData((prev) => ({ ...prev, immobileId: "" }));
        return;
      }

      setLoadingImmobili(true);
      try {
        const immobiliData = await apiRequest(
          `/rsa/${formData.rsaId}/immobili`,
        );
        setImmobiliList(immobiliData.value || []);
        if (formData.immobileId) {
          const found = (immobiliData.value || []).find(
            (i) => i.id === formData.immobileId,
          );
          if (!found) {
            setFormData((prev) => ({ ...prev, immobileId: "" }));
          }
        }
      } catch (error) {
        console.error("Error loading immobili:", error);
      } finally {
        setLoadingImmobili(false);
      }
    };

    loadRsaImmobili();
  }, [formData.rsaId, apiRequest]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.nome.trim()) newErrors.nome = "Il nome è obbligatorio";
    if (!formData.rsaId) newErrors.rsaId = "La RSA è obbligatoria";
    if (formData.tipoImpianto === "")
      newErrors.tipoImpianto = "Il tipo è obbligatorio";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showToast("Correggi gli errori nel form", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        tipoImpianto: Number(formData.tipoImpianto),
        stato: Number(formData.stato),
        immobileId: formData.immobileId || null,
        fornitoreAcquistoId: formData.fornitoreAcquistoId || null,
        fornitoreManutenzioneId: formData.fornitoreManutenzioneId || null,
        dataInstallazione: formData.dataInstallazione || null,
        dataScadenzaGaranzia: formData.dataScadenzaGaranzia || null,
        potenzaInstallata:
          formData.potenzaInstallata !== ""
            ? Number(formData.potenzaInstallata)
            : null,
        numeroPraticaGse: formData.numeroPraticaGse || null,
        regimeIncentivo:
          formData.regimeIncentivo !== ""
            ? Number(formData.regimeIncentivo)
            : null,
        dataAllaccioRete: formData.dataAllaccioRete || null,
        gestoreRete: formData.gestoreRete || null,
      };

      if (isEdit) {
        await apiRequest(`/impianti/${id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        showToast("Impianto aggiornato con successo", "success");
        navigate(`/impianti/${id}`);
      } else {
        const result = await apiRequest("/impianti", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        showToast("Impianto creato con successo", "success");
        navigate(`/impianti/${result.id}`);
      }
    } catch (error) {
      console.error("Error saving impianto:", error);
      showToast(error.message || "Errore nel salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? "Modifica Impianto" : "Nuovo Impianto"}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEdit
              ? "Modifica i dati dell'impianto"
              : "Inserisci i dati del nuovo impianto"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informazioni Base
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome / Descrizione *
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                className={`w-full rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 ${errors.nome ? "border-red-300" : "border-gray-300"}`}
                placeholder="Es: Impianto Fotovoltaico Tetto Nord"
              />
              {errors.nome && (
                <p className="mt-1 text-sm text-red-600">{errors.nome}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo Impianto *
              </label>
              <select
                name="tipoImpianto"
                value={formData.tipoImpianto}
                onChange={handleChange}
                className={`w-full rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 ${errors.tipoImpianto ? "border-red-300" : "border-gray-300"}`}
              >
                {TIPO_IMPIANTO_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.icon} {opt.label}
                  </option>
                ))}
              </select>
              {errors.tipoImpianto && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.tipoImpianto}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stato
              </label>
              <select
                name="stato"
                value={formData.stato}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                {STATO_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.icon} {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marca / Produttore
              </label>
              <input
                type="text"
                name="marca"
                value={formData.marca}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Modello
              </label>
              <input
                type="text"
                name="modello"
                value={formData.modello}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Ubicazione
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                RSA *
              </label>
              <select
                name="rsaId"
                value={formData.rsaId}
                onChange={handleChange}
                className={`w-full rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 ${errors.rsaId ? "border-red-300" : "border-gray-300"}`}
              >
                <option value="">Seleziona RSA...</option>
                {rsaList.map((rsa) => (
                  <option key={rsa.id} value={rsa.id}>
                    {rsa.nome}
                  </option>
                ))}
              </select>
              {errors.rsaId && (
                <p className="mt-1 text-sm text-red-600">{errors.rsaId}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Immobile
              </label>
              <select
                name="immobileId"
                value={formData.immobileId}
                onChange={handleChange}
                disabled={!formData.rsaId || loadingImmobili}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
              >
                <option value="">
                  {!formData.rsaId
                    ? "Seleziona prima la RSA"
                    : loadingImmobili
                      ? "Caricamento..."
                      : "Nessuno"}
                </option>
                {immobiliList.map((imm) => (
                  <option key={imm.id} value={imm.id}>
                    {imm.nome}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Opzionale: seleziona l'immobile di riferimento
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Fornitori
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fornitore Acquisto
              </label>
              <select
                name="fornitoreAcquistoId"
                value={formData.fornitoreAcquistoId}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Nessuno</option>
                {fornitoriList.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fornitore Manutenzione
              </label>
              <select
                name="fornitoreManutenzioneId"
                value={formData.fornitoreManutenzioneId}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Nessuno</option>
                {fornitoriList.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Date</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Installazione
              </label>
              <input
                type="date"
                name="dataInstallazione"
                value={formData.dataInstallazione}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scadenza Garanzia
              </label>
              <input
                type="date"
                name="dataScadenzaGaranzia"
                value={formData.dataScadenzaGaranzia}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </Card>

        {formData.tipoImpianto == TIPO_FOTOVOLTAICO && (
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Dati Impianto Fotovoltaico
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Potenza Installata (kWp)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="potenzaInstallata"
                  value={formData.potenzaInstallata}
                  onChange={handleChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Es: 19.8"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numero Pratica/Convenzione GSE
                </label>
                <input
                  type="text"
                  name="numeroPraticaGse"
                  value={formData.numeroPraticaGse}
                  onChange={handleChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Regime GSE
                </label>
                <select
                  name="regimeIncentivo"
                  value={formData.regimeIncentivo}
                  onChange={handleChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Non specificato</option>
                  {REGIME_INCENTIVO_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Allaccio Rete
                </label>
                <input
                  type="date"
                  name="dataAllaccioRete"
                  value={formData.dataAllaccioRete}
                  onChange={handleChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gestore di Rete
                </label>
                <input
                  type="text"
                  name="gestoreRete"
                  value={formData.gestoreRete}
                  onChange={handleChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Es: e-distribuzione"
                />
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Note</h2>
          <textarea
            name="note"
            value={formData.note}
            onChange={handleChange}
            rows={4}
            className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
            placeholder="Note aggiuntive..."
          />
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Annulla
          </Button>
          <Button type="submit" disabled={saving}>
            {saving
              ? "Salvataggio..."
              : isEdit
                ? "Salva Modifiche"
                : "Crea Impianto"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default NuovoImpiantoPage;
